import { useContext } from "react";
import { useDeleteCustomMapGeometryMutation } from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { useDeleteModal } from "../Common/DeleteModal";

interface UseDeleteCustomMapGeometryModalProps {
  geometryId: string;
  customMapId: string;
  objectType: string;
  objectName: string;
  updateMap: () => void;
  onDelete: () => void;
}

export const useDeleteCustomMapGeometryModal = ({
  geometryId,
  customMapId,
  objectType,
  objectName,
  updateMap,
  onDelete,
}: UseDeleteCustomMapGeometryModalProps) => {
  const { account } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const [deleteGeometry, { loading }] = useDeleteCustomMapGeometryMutation({
    variables: { id: geometryId, customMapId },
    onCompleted: () => {
      track("Deleted custom map geometry", {
        geometryLabel: objectName,
        customMapName: objectType,
      });
      updateMap();
      addSuccessToast(`The ${objectType} was successfully deleted`);
      onDelete();
    },
    onError: () => {
      addErrorToast(
        "There was an error deleting this object, please try again. If the problem persists, email us at support@withforerunner.com",
      );
    },
  });

  const confirmations = [
    `All associated data with the ${objectType} will be deleted.`,
    `The ${objectType} will be removed from the dashboard for all users.`,
    ...(account?.publicPortal.enabled
      ? [
          `The ${objectType} will no longer be accessible from your public website.`,
        ]
      : []),
  ];

  const [showModal, hideModal] = useDeleteModal({
    headerText: `Delete ${objectType}`,
    subheaderText: objectName,
    confirmations,
    confirmationPreamble: `By deleting the selected ${objectType}, you confirm that:`,
    size: "large",
    deleteButtonText: `Delete ${objectType}`,
    loading,
    onDelete: async () => {
      await deleteGeometry();
    },
    dependencies: [
      geometryId,
      customMapId,
      objectType,
      objectName,
      updateMap,
      onDelete,
      loading,
    ],
  });

  return [showModal, hideModal] as const;
};
